import type { OrderList, SupplierOrderSnapshot } from "./type/orderList";

/**
 * 訂單詳細「訂單摘要」用到的金額。只吃 snapshot 裡會用到的欄位；
 * 7.102 以前的舊快照沒有 order_amount_cached／floor_fee_excluded，一律可選。
 */
type AmountOrderList = Partial<
  Pick<
    OrderList,
    | "discount"
    | "gas_discount"
    | "total_price_cached"
    | "order_amount_cached"
    | "floor_fee_excluded"
    | "floor_fee"
    | "floor_fee_discount_amount"
    | "coupon_discount_amount"
    | "coupon_supplier_discount_amount"
  >
>;

export interface DetailAmountsInput {
  items?: Array<Pick<SupplierOrderSnapshot["items"][number], "subtotal">>;
  cylinders?: Array<Pick<SupplierOrderSnapshot["cylinders"][number], "subtotal" | "customer_action_type">>;
  mortgages?: Array<Pick<SupplierOrderSnapshot["mortgages"][number], "subtotal">>;
  usage_fees?: Array<Pick<SupplierOrderSnapshot["usage_fees"][number], "money">>;
  refunds?: Array<Pick<SupplierOrderSnapshot["refunds"][number], "amount">>;
  payups?: Array<Pick<SupplierOrderSnapshot["payups"][number], "amount">>;
  order_list?: AmountOrderList;
}

export interface DetailAmounts {
  /** 應付金額（客戶實付） */
  payable: number;
  /** 訂單金額（total_price_cached）；退款上限跟著它走，RefundCard 用這個 */
  orderTotal: number;
  /** 「樓層費」那一行（毛額） */
  floorFeeLine: number;
  /** 「樓層費折抵」那一行；不含樓層費的單不使用折讓，一律 0（不顯示） */
  floorFeeDiscountLine: number;
  /** 瓦斯行吸收的優惠券折抵（攤在瓦斯品項上顯示） */
  couponDiscount: number;
  /** 逐行加總；客戶實付與訂單金額都沒有時的退路 */
  computedTotal: number;
  /** 實付金額（已完成且有收款時顯示）；不含樓層費的單有收款時＝收款加總＋樓層費毛額 */
  paidTotal: number;
}

const num = (v: unknown) => Number(v || 0);

/**
 * 客戶實付：order_amount_cached；舊快照沒有就退回訂單金額 total_price_cached；都沒有回 null。
 * 訂單詳細的應付與下單完成頁的總額共用這裡。
 *
 * 平台券：已定案（2026-10-04 Q1＝B）這次不扣，客戶實付只含瓦斯行自家券的折抵；
 * 有用平台券的單，這裡的金額會比客戶下單時實際要付的多一張平台券（另記待辦）。
 */
export function customerPayableFromOrderList(
  orderList: Partial<Pick<OrderList, "order_amount_cached" | "total_price_cached">> | null | undefined
): number | null {
  const amount = orderList?.order_amount_cached ?? orderList?.total_price_cached;
  return amount != null ? Number(amount) : null;
}

export function computeDetailAmounts(snapshot: DetailAmountsInput | null | undefined): DetailAmounts {
  const ol = snapshot?.order_list;
  const excluded = ol?.floor_fee_excluded === true;

  const itemsSubtotal = (snapshot?.items ?? []).reduce((s, it) => s + num(it.subtotal), 0);
  // 鋼瓶買賣：客戶購入(+) / 客戶賣出(−)
  const cylinderNet = (snapshot?.cylinders ?? []).reduce(
    (s, c) => s + (c.customer_action_type === "客戶賣出" ? -num(c.subtotal) : num(c.subtotal)),
    0
  );
  // 押退桶：押桶/借桶/還桶/寄桶 一律相加（對齊 total 公式）
  const mortgageTotal = (snapshot?.mortgages ?? []).reduce((s, m) => s + num(m.subtotal), 0);
  const usageFeeTotal = (snapshot?.usage_fees ?? []).reduce((s, u) => s + num(u.money), 0);
  // 退氣/存氣、折扣/瓦斯折扣：折抵(−)
  const refundTotal = (snapshot?.refunds ?? []).reduce((s, r) => s + num(r.amount), 0);
  const discountTotal = num(ol?.discount) + num(ol?.gas_discount);

  const floorFeeLine = num(ol?.floor_fee);
  // 不含樓層費的單：樓層費折讓不使用、不顯示
  const floorFeeDiscountLine = excluded ? 0 : num(ol?.floor_fee_discount_amount);
  const couponDiscount = num(ol?.coupon_supplier_discount_amount);

  // 各行加總：品項 + 鋼瓶買賣 + 押退桶 + 使用費 + 樓層費 − 退氣 − 折扣 − 樓層費折抵 − 優惠券。
  // 含樓層費的單＝訂單金額公式；不含樓層費的單＝客戶實付（樓層費毛額加回、不扣折讓）。
  const computedTotal =
    itemsSubtotal +
    cylinderNet +
    mortgageTotal +
    usageFeeTotal +
    floorFeeLine -
    refundTotal -
    discountTotal -
    floorFeeDiscountLine -
    couponDiscount;

  const payable = customerPayableFromOrderList(ol) ?? computedTotal;
  const orderTotal =
    ol?.total_price_cached != null
      ? Number(ol.total_price_cached)
      : excluded
        ? computedTotal - floorFeeLine
        : computedTotal;

  // 實付金額：瓦斯行端同步進來的收款明細加總。
  // 不含樓層費的單（已定案 2026-10-04 Q2＝A）：樓層費由配送員當場另收、系統收款沒記，
  // 有收款時再加樓層費毛額（例：收款 1,550＋樓層費 100＝1,650，與應付一致）。
  // 沒收款時維持 0、不加樓層費。畫面只在「已完成且有收款」時顯示實付（條件在 orderDetail.tsx）。
  const payups = snapshot?.payups ?? [];
  const payupSum = payups.reduce((s, p) => s + num(p.amount), 0);
  const paidTotal = excluded && payups.length > 0 ? payupSum + floorFeeLine : payupSum;

  return { payable, orderTotal, floorFeeLine, floorFeeDiscountLine, couponDiscount, computedTotal, paidTotal };
}
